/** Jalali helpers. API storage stays ISO/UTC. */

const FA_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
const EN_DIGITS = "0123456789";

export function toFaDigits(value: string | number): string {
  return String(value).replace(/\d/g, (d) => FA_DIGITS[Number(d)] ?? d);
}

export function toEnDigits(value: string): string {
  return value.replace(/[۰-۹]/g, (d) => EN_DIGITS[FA_DIGITS.indexOf(d)] ?? d);
}

export interface JalaliParts {
  readonly jy: number;
  readonly jm: number;
  readonly jd: number;
}

export interface JalaliDateTimeParts extends JalaliParts {
  readonly hour: number;
  readonly minute: number;
}

function div(a: number, b: number): number {
  return Math.trunc(a / b);
}

/** Gregorian → Jalali (jalaali algorithm). */
export function toJalali(gy: number, gm: number, gd: number): JalaliParts {
  const gdm = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
  const gy2 = gm > 2 ? gy + 1 : gy;
  let days =
    355666 +
    365 * gy +
    div(gy2 + 3, 4) -
    div(gy2 + 99, 100) +
    div(gy2 + 399, 400) +
    gd +
    gdm[gm - 1]!;
  let jy = -1595 + 33 * div(days, 12053);
  days %= 12053;
  jy += 4 * div(days, 1461);
  days %= 1461;
  if (days > 365) {
    jy += div(days - 1, 365);
    days = (days - 1) % 365;
  }
  const jm = days < 186 ? 1 + div(days, 31) : 7 + div(days - 186, 30);
  const jd = 1 + (days < 186 ? days % 31 : (days - 186) % 30);
  return { jy, jm, jd };
}

/** Jalali → Gregorian. */
export function toGregorian(
  jy: number,
  jm: number,
  jd: number,
): { gy: number; gm: number; gd: number } {
  jy += 1595;
  let days =
    -355668 +
    365 * jy +
    div(jy, 33) * 8 +
    div((jy % 33) + 3, 4) +
    jd +
    (jm < 7 ? (jm - 1) * 31 : (jm - 7) * 30 + 186);
  let gy = 400 * div(days, 146097);
  days %= 146097;
  if (days > 36524) {
    gy += 100 * div(--days, 36524);
    days %= 36524;
    if (days >= 365) days++;
  }
  gy += 4 * div(days, 1461);
  days %= 1461;
  if (days > 365) {
    gy += div(days - 1, 365);
    days = (days - 1) % 365;
  }
  const gd = days + 1;
  const salA = [
    0,
    31,
    (gy % 4 === 0 && gy % 100 !== 0) || gy % 400 === 0 ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];
  let gm = 0;
  let dayCount = gd;
  for (gm = 1; gm <= 12 && dayCount > salA[gm]!; gm++) {
    dayCount -= salA[gm]!;
  }
  return { gy, gm, gd: dayCount };
}

export function jalaliMonthLength(jy: number, jm: number): number {
  if (jm <= 6) return 31;
  if (jm <= 11) return 30;
  // Esfand: leap if ((jy+12)%33)%4 === 1 in common algorithm variant
  const cycle = ((jy + 12) % 33) % 4;
  return cycle === 1 ? 30 : 29;
}

export const JALALI_MONTHS_FA = Object.freeze([
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
] as const);

export function formatJalaliDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const j = toJalali(date.getFullYear(), date.getMonth() + 1, date.getDate());
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return toFaDigits(
    `${j.jy}/${String(j.jm).padStart(2, "0")}/${String(j.jd).padStart(2, "0")} · ${hh}:${mm}`,
  );
}

export function formatJalaliDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const j = toJalali(date.getFullYear(), date.getMonth() + 1, date.getDate());
  const month = JALALI_MONTHS_FA[j.jm - 1] ?? String(j.jm);
  return toFaDigits(`${j.jd} ${month} ${j.jy}`);
}

export function dateToJalaliParts(date: Date): JalaliDateTimeParts {
  const j = toJalali(date.getFullYear(), date.getMonth() + 1, date.getDate());
  return { ...j, hour: date.getHours(), minute: date.getMinutes() };
}

export function jalaliPartsToDate(parts: JalaliDateTimeParts): Date {
  const g = toGregorian(parts.jy, parts.jm, parts.jd);
  return new Date(g.gy, g.gm - 1, g.gd, parts.hour, parts.minute, 0, 0);
}

export function jalaliPartsToIso(parts: JalaliDateTimeParts): string {
  return jalaliPartsToDate(parts).toISOString();
}

/** Convert a Jalali calendar day to the Gregorian YYYY-MM-DD wire format. */
export function jalaliPartsToGregorianDate(parts: JalaliParts): string {
  const g = toGregorian(parts.jy, parts.jm, parts.jd);
  return `${String(g.gy).padStart(4, "0")}-${String(g.gm).padStart(2, "0")}-${String(g.gd).padStart(2, "0")}`;
}

/** Convert the API Gregorian YYYY-MM-DD wire value to Jalali calendar parts. */
export function gregorianDateToJalaliParts(
  value: string | null | undefined,
): JalaliParts | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? "");
  if (!match) return null;
  const gy = Number(match[1]);
  const gm = Number(match[2]);
  const gd = Number(match[3]);
  if (gm < 1 || gm > 12 || gd < 1 || gd > 31) return null;
  return toJalali(gy, gm, gd);
}

/** Format an API Gregorian YYYY-MM-DD value as an explicitly Jalali date. */
export function formatJalaliGregorianDate(
  value: string | null | undefined,
): string {
  const parts = gregorianDateToJalaliParts(value);
  if (!parts) return "—";
  return toFaDigits(
    `${parts.jy}/${String(parts.jm).padStart(2, "0")}/${String(parts.jd).padStart(2, "0")}`,
  );
}

export function isoToJalaliParts(
  iso: string | null | undefined,
): JalaliDateTimeParts | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return dateToJalaliParts(date);
}

/** Convert datetime-local (legacy) to ISO. */
export function localInputToIso(localValue: string): string {
  const date = new Date(localValue);
  if (Number.isNaN(date.getTime())) throw new Error("تاریخ نامعتبر است.");
  return date.toISOString();
}

export function formatJalaliFromLocalInput(localValue: string): string {
  try {
    return formatJalaliDateTime(localInputToIso(localValue));
  } catch {
    return "—";
  }
}

export function nowJalaliParts(): JalaliDateTimeParts {
  return dateToJalaliParts(new Date());
}
